import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useLayoutEffect, useMemo, useRef } from "react";
import "./websiteBuilderPreview.scss";
import "../designSpace/styles/website.scss";
import { WEBSITE_BUILDER_Z_LAYERS } from "../utility/websiteBuilderZLayers";
import { websiteBuilderGeneratePageRootFragment } from "pages/secure/facility/websiteBuilder/editor/website/websiteBuilderGenerateWebsite";
import classNames from "classnames";
import { websiteBuilderSelectPage } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { websiteBuilderSyncBackgroundVideoMotion } from "pages/secure/facility/websiteBuilder/editor/website/shared/sectionBackground";

export function WebsiteBuilderPreview() {
  const dispatch = useAppDispatch();

  const logoUrl = useAppSelector(store => store.websiteBuilderStore.config.website?.logo_url);

  const header = useAppSelector(store => store.websiteBuilderStore.editor.header);
  const footer = useAppSelector(store => store.websiteBuilderStore.editor.footer);
  const rootCssVariables = useAppSelector(store => store.websiteBuilderStore.editor.rootCssVariables);
  const pages = useAppSelector(store => store.websiteBuilderStore.editor.pages);
  const pageMap = useAppSelector(store => store.websiteBuilderStore.editor.pageMap);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const navigationMenuItems = useAppSelector(store => store.websiteBuilderStore.editor.navigationMenuItems);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const forms = useAppSelector(store => store.websiteBuilderStore.editor.forms);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const activePage = pageMap.get(activePageId);

  function selectPreviewTab(tabHeaderItem: HTMLDivElement) {
    const tabHeader = tabHeaderItem.parentElement;
    const tabset = tabHeader?.parentElement;

    if (
      !tabHeader?.classList.contains("website-builder-tabset-tab-header") ||
      !tabset?.classList.contains("website-builder-tabset") ||
      tabHeaderItem.classList.contains("website-builder-tabset-tab-header-item-active")
    ) {
      return;
    }

    const tabHeaderItems = Array.from(tabHeader.children).filter(
      (child): child is HTMLDivElement =>
        child instanceof HTMLDivElement && child.classList.contains("website-builder-tabset-tab-header-item"),
    );
    const tabs = Array.from(tabset.children).filter(
      (child): child is HTMLDivElement =>
        child instanceof HTMLDivElement && child.classList.contains("website-tab"),
    );

    const tabHeaderItemIndex = tabHeaderItems.findIndex(item => item === tabHeaderItem);
    const nextTab = tabs[tabHeaderItemIndex];

    if (nextTab != null) {
      tabHeaderItems.forEach(item => {
        item.classList.toggle("website-builder-tabset-tab-header-item-active", item === tabHeaderItem);
      });
      tabs.forEach(tab => {
        tab.classList.toggle("website-tab-active", tab === nextTab);
      });
    }
  }

  const previewContentRef = useRef<HTMLDivElement>(null);

  const previewHTML = useMemo(() => {
    const rootFragment = websiteBuilderGeneratePageRootFragment(header, activePage?.root, footer, logoUrl, rootCssVariables, navigationMenuItems, pages, activePageId, forms, iconAssets, true);
    const div = document.createElement("div");

    div.appendChild(rootFragment);

    return div.innerHTML;
  }, [header, activePage?.root, footer, logoUrl, rootCssVariables, navigationMenuItems, pages, activePageId, forms, iconAssets]);

  useLayoutEffect(() => {
    const content = previewContentRef.current;

    if (content == null) {
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    websiteBuilderSyncBackgroundVideoMotion(content, prefersReducedMotion.matches);
  }, [previewHTML]);

  return (
    <div
      className="website-builder-preview-outer-frame"
      style={{
        zIndex: WEBSITE_BUILDER_Z_LAYERS.previewOuterFrame,
      }}
      data-preview-device={deviceView}
    >
      <div
        className={classNames({
          "preview-container-desktop": deviceView === "desktop",
          "preview-container-tablet": deviceView === "tablet",
          "preview-container-mobile": deviceView === "mobile",
        })}
      >
        <div
          ref={previewContentRef}
          className={classNames({
            "preview-desktop": deviceView === "desktop",
            "preview-tablet": deviceView === "tablet",
            "preview-mobile": deviceView === "mobile",
          })}
          dangerouslySetInnerHTML={{ __html: previewHTML }}
          onClickCapture={e => {
            e.stopPropagation();
            e.preventDefault();

            const target = e.target;

            if (!(target instanceof Element)) {
              return;
            }

            const anchor = target.closest<HTMLAnchorElement>("a[data-preview-page-id]");
            const pageId = Number(anchor?.dataset.previewPageId);

            if (pageMap.has(pageId)) {
              dispatch(websiteBuilderSelectPage({ activePageId: pageId }));
            }

            const tabHeaderItem = target.closest<HTMLDivElement>(".website-builder-tabset-tab-header-item");

            if (tabHeaderItem != null) {
              selectPreviewTab(tabHeaderItem);
              return;
            }
          }}
        />
      </div>
    </div>
  );
}
